import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { SidebarProvider } from './Layout/SidebarContext.js';
import Login from './Login/Components/Login.js';
import Home from './Home/Components/Home.js';
import Layout from './Layout/Component/Layout.js';
import Quotes from './Quotes/Components/Quotes.js';
import Organization from './Configuration/Organization.js';
import UserTypes from './Configuration/UserTypes.js';
import Solution from './Configuration/Solution.js';
import Questions from './Configuration/Questions.js';
import Options from './Configuration/Options.js';
import TimePrice from './Configuration/TimePrice.js';
import BankDetails from './Configuration/BankDetails.js';
import Configure from './Configuration/Configure.js';
import DisplayManual from './UserManual/Components/DisplayManual';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true); // To handle the loading state
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [initialized, setInitialized] = useState(false);

  // Initialize authentication state from localStorage on first load only
  useEffect(() => {
    const authState = localStorage.getItem('isAuthenticated');
    if (authState === 'true') {
      setIsAuthenticated(true);
    }
    setLoading(false);
    setInitialized(true);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
    // Store current timestamp as login time
    localStorage.setItem('loginTime', Date.now().toString());
  };

  const handleLogout = () => {
    console.log("LOGGING OUT...");
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('lastVisitedPage');
    localStorage.removeItem('loginTime');
    setIsAuthenticated(false);
  };

  // Update last visited page whenever the page changes
  useEffect(() => {
    // Only track last visited page if authenticated
    if (isAuthenticated && initialized) {
      const currentPath = window.location.pathname;
      if (currentPath !== '/') {
        localStorage.setItem('lastVisitedPage', currentPath);
      }
    }
  }, [isAuthenticated, initialized]);

  // Idle timeout detection
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const idleTimeout = 6 * 60 * 1000;  // 6 minutes
    const resetTimer = () => { setLastActivityTime(Date.now()); };
    
    document.addEventListener('mousemove', resetTimer);
    document.addEventListener('keypress', resetTimer);
    document.addEventListener('click', resetTimer);
    document.addEventListener('scroll', resetTimer);
    
    const checkInactivity = setInterval(() => {
      if (Date.now() - lastActivityTime > idleTimeout) {
        handleLogout();
      }
    }, 60000);

    return () => {
      document.removeEventListener('mousemove', resetTimer);
      document.removeEventListener('keypress', resetTimer);
      document.removeEventListener('click', resetTimer);
      document.removeEventListener('scroll', resetTimer);
      clearInterval(checkInactivity);
    };
  }, [isAuthenticated, lastActivityTime]);

  // Handle tab close vs refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleBeforeUnload = (e) => {
      // For tab/browser close, we do nothing special
      // Authentication will persist in localStorage
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated]);

  // Redirect to last visited page if necessary
  useEffect(() => {
    if (isAuthenticated && initialized && window.location.pathname === '/') {
      const lastPage = localStorage.getItem('lastVisitedPage');
      if (lastPage && lastPage !== '/') {
        window.location.replace(lastPage);
      }
    }
  }, [isAuthenticated, initialized]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <SidebarProvider>
      <Router>
        <Routes>
          {/* Public Route */}
          <Route 
            path="/" 
            element={isAuthenticated ? <Navigate to="/home" /> : <Login onLogin={handleLogin} />} 
          />

          {/* Protected Routes */}
          <Route 
            path="/home" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Home /></Layout> : <Navigate to="/" />} 
          />
          <Route
            path="/quotes"
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Quotes onLogout={handleLogout} /></Layout> : <Navigate to="/" />}
          />
          {/* <Route
            path="/manual"
            element={isAuthenticated ? <Layout onLogout={handleLogout}><DisplayManual onLogout={handleLogout}/></Layout>: <Navigate to="/" />}
          /> */}
          <Route 
            path="/organization" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Organization /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/usertypes" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><UserTypes /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/solution" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Solution /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/questions" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Questions /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/options" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Options /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/timeprice" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><TimePrice /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/bankdetails" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><BankDetails /></Layout> : <Navigate to="/" />} 
          />
          <Route 
            path="/configure" 
            element={isAuthenticated ? <Layout onLogout={handleLogout}><Configure /></Layout> : <Navigate to="/" />} 
          />
        </Routes>
      </Router>
    </SidebarProvider>
  );
}

export default App;